import { useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';

// "Refaire la transcription": Speakr transcribes the recording again from its
// audio, and PMO collects the new transcript as it did the first one. Inline in
// the transcript window; in a small dialog beside a transcript that failed.

// Static keys so the i18n checker can resolve them.
const ERROR_KEY: Record<string, string> = {
  busy: 'transcript.redoBusy',
  no_audio: 'transcript.redoNoAudio',
  no_account: 'transcript.redoNoAccount',
};

export function RedoTranscriptForm({ transcriptionId, onDone, onCancel }: {
  transcriptionId: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [speakers, setSpeakers] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = Number(speakers);
  const valid = speakers === '' || (Number.isInteger(n) && n >= 1 && n <= 20);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await api.redoTranscription(transcriptionId, speakers === '' ? null : n);
      onDone();
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(code && ERROR_KEY[code] ? t(ERROR_KEY[code]) : t('transcript.redoFailed', { error: (err as Error).message }));
      setSending(false);
    }
  };

  return (
    <div className="redo-transcript">
      <div className="redo-transcript-help">{t('transcript.redoHelp')}</div>
      <label className="redo-transcript-row">
        <span>{t('transcript.redoSpeakers')}</span>
        <input
          className="form-input redo-transcript-count"
          type="number"
          inputMode="numeric"
          min={1}
          max={20}
          value={speakers}
          placeholder={t('transcript.redoSpeakersAuto')}
          onChange={e => setSpeakers(e.target.value.trim())}
        />
      </label>
      <div className="redo-transcript-hint">{t('transcript.redoSpeakersHint')}</div>
      {error && <div className="transcript-error">{error}</div>}
      <div className="redo-transcript-actions">
        <button type="button" className="btn btn-accent btn-sm" disabled={sending || !valid} onClick={send}>
          {sending ? t('transcript.redoSending') : t('transcript.redoGo')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={sending} onClick={onCancel}>{t('transcript.redoCancel')}</button>
      </div>
    </div>
  );
}

/** "Relancer" beside a transcript that failed: opens the form in a small dialog. */
export function RedoTranscriptButton({ transcriptionId, className }: { transcriptionId: number; className: string }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} onClick={e => { e.stopPropagation(); setOpen(true); }}>
        <RotateCcw size={11} /> {t('transcript.redoRetry')}
      </button>
      {open && (
        <div className="modal-overlay" style={{ zIndex: 300 }} onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('transcript.redo')}</h3>
              <button className="close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body redo-transcript-dialog">
              <RedoTranscriptForm transcriptionId={transcriptionId} onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
